import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; // Import useParams to get the ID
import { Button, Alert, Spinner } from 'flowbite-react';
import api from '../../../services/api/axiosConfig';
import PageHeading from '../../../components/PageHeading';
import errorHandler from '../../../utils/errorHandler';
import CustomerForm from '../components/CustomerForm';
import Breadcrumb from '../../dashboard/pages/components/BreadCrumb';
import customerValidation from '../utils/customerValidation';
import { initialCustomerData } from '../data/customerModel';
import { toast } from 'react-toastify';
import API_ENDPOINTS, { buildEndpoint } from '../../../services/api/endpoints';

const UpdateCustomerPage = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Get the customer ID from the URL

    // State for form data, initialized to empty, will be populated on fetch
    const [formData, setFormData] = useState(initialCustomerData);

    const breadcrumbItems = [
        { label: 'Dashboard', url: '/dashboard' },
        { label: 'Customers', url: '/dashboard/customer-list' },
        { label: 'Edit Customer', url: '#', current: true } // Mark as current page
    ];

    const [errors, setErrors] = useState({}); // For field-specific validation errors
    const [loading, setLoading] = useState(false); // For form submission loading state
    const [fetchingCustomer, setFetchingCustomer] = useState(true); // For initial data fetch loading state
    const [fetchError, setFetchError] = useState(null); // For errors during initial data fetch
    const [generalError, setGeneralError] = useState(null); // For general API errors during submission
    const [successMessage, setSuccessMessage] = useState(null); // For success messages

    useEffect(() => {
        document.title = 'CPA - Update Customer';
        // You might want to verify user authentication here as well
        // if (!localStorage.getItem('access_token')) { navigate('/'); }

        const fetchCustomerDetails = async () => {
            setFetchingCustomer(true);
            setFetchError(null);
            try {
                const response = await api.get(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.SHOW,{id})); // Fetch customer by ID

                if (response.data.statuscode==201 && response.data.body) {
                    const customerData = response.data.body;
                    setFormData({
                        // Ensure all fields match your API's expected structure
                        firstName: customerData.FirstName || '',
                        lastName: customerData.LastName || '',
                        email: customerData.Email || '',
                        phone: customerData.Phone || '',
                        streetAddress: customerData.StreetAddress || '',
                        city: customerData.City || '',
                        state: customerData.State || '',
                        zipCode: customerData.ZipCode || '',
                        notes: customerData.notes || '',
                    });
                } else {
                    setFetchError(response.data.message || 'Customer not found or failed to fetch details.');
                }
            } catch (error) {
                console.error('Error fetching customer details:', error);
                errorHandler(error,setErrors);
            } finally {
                setFetchingCustomer(false);
            }
        };

        if (id) {
            fetchCustomerDetails();
        } else {
            setFetchingCustomer(false);
            setFetchError('Customer ID is missing from the URL.');
        }

    }, [id, navigate]); // Re-fetch if ID changes

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
        setGeneralError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        setGeneralError(null);
        setSuccessMessage(null);

        const validationErrors = customerValidation(formData);
        
  
        if (Object.keys(validationErrors).length === 0) {

            try {
                const payload = {
                    ...formData,
                    // Strip all non-numeric characters from the phone number
                    phone: formData.phone.replace(/\D/g, ''),

                };
                // Use PUT or PATCH for updates. PATCH is generally preferred for partial updates.
                const response = await api.put(`/customer/${id}`, payload);

                console.log('Customer updated successfully:', response.data);
                toast.success(response.data.message ||"Customer updated successfully.");
                
                navigate('/dashboard/customer-list')
                
            } catch (error) {
                console.error('Failed to update customer:', error);
                errorHandler(error, setErrors);

            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            setErrors(validationErrors);
            setGeneralError('Please fix the validation errors before submitting.');
        }
    };

    // Render loading state for initial fetch
    if (fetchingCustomer) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Spinner size="xl" /> <span className="ml-3 text-lg">Loading Customer...</span>
            </div>
        );
    }

    // Render error state for initial fetch
    if (fetchError) {
        return (
            <div className="p-4 sm:p-6 lg:p-8">
                <Alert color="failure" onDismiss={() => setFetchError(null)}>
                    <span className="font-medium">Error loading customer:</span> {fetchError}
                </Alert>
                <div className="text-center mt-4">
                    <Button onClick={() => navigate('/dashboard/customers')}>Go to Customer List</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />

            <PageHeading value={`Update Customer: ${formData.firstName+' '+formData.lastName}`} />

            {generalError && (
                <Alert color="failure" onDismiss={() => setGeneralError(null)} className="mb-4">
                    <span className="font-medium">Error!</span> {generalError}
                </Alert>
            )}
            {successMessage && (
                <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
                    <span className="font-medium">Success!</span> {successMessage}
                </Alert>
            )}

            <CustomerForm
                formData={formData}
                handleChange={handleChange}
                errors={errors}
                loading={loading}
                handleSubmit={handleSubmit}
                submitButtonText="Update Customer"
                submitButtonTextSubmitting="Updating Customer..."

            />
        </div>
    );
};

export default UpdateCustomerPage;
